import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { axios } from "../lib/request";
import { answerQueryKey, infiniteAnswerCommentsUri, infiniteAnswerCommentsQueryKey } from "../lib/refresh";

export const fetchAnswer = async (id, game_id) => {
    const url = `${process.env.REACT_APP_API_ANSWER_RETRIEVE_URL}/${id}.json?game_id=${game_id}`;
    const { data } = await axios.get(url);
    return data;
};

export const useAnswerDataQuery = (id) => {
    const { game_id } = useParams();
    return useQuery(answerQueryKey(id), () => fetchAnswer(id, game_id), {
        suspense: true,
    });
};

export const useAnswerQuery = (id) => {
    const { data } = useAnswerDataQuery(id);
    return data.answer;
};

export const fetchCommentStream = async (answer_id, token) => {
    try {
        const params = { token, per_page: 50 };
        const response = await axios.get(infiniteAnswerCommentsUri(answer_id), { params });
        const { data = {} } = response || {};

        // Filter out empty comments
        data.comments = (data.comments || []).filter(c => c?.comment !== null)
        return data;
    } catch (e) {
        console.error(e)
    }
};

const fetchAllCommentsStreams = ({ pageParam }, answer_id) => {
    return fetchCommentStream(answer_id, pageParam);
}

const deleteComment = (comment_id, game_id, answer_id) => axios.delete(
    `${process.env.REACT_APP_API_ANSWERS_COMMENTS_UPDATE_URL}/${comment_id}.json?game_id=${game_id}&answer_id=${answer_id}`
)

export const useInfiniteAnswersComments = (answer_id) => {
    return useInfiniteQuery(infiniteAnswerCommentsQueryKey(answer_id), config => fetchAllCommentsStreams(config, answer_id), {
        getNextPageParam: (lastPage, pages) => {
            const ids = (lastPage?.comments || []).map(({ id }) => parseInt(id));
            const { count = 0 } = lastPage || {};
            const loaded = pages.reduce((sum, page = []) => (sum += page?.length), 0);

            if (lastPage?.comments?.length === 0 || count <= loaded) {
                return undefined;
            }

            return Math.min(...ids);
        },
        suspense: false,
    });
}

export const useDeleteAnswersCommentMutation = (answer_id) => {
    const { game_id } = useParams()
    const { data: oldPagesArray } = useInfiniteAnswersComments(answer_id)
    const queryClient = useQueryClient()
    const { mutateAsync, isLoading } = useMutation(
        (comment) => deleteComment(comment.id, game_id, answer_id),
        {
            onMutate: (data) => {
                // Filter out deleted comment
                const newPagesArray =
                    oldPagesArray?.pages.map((page) => {
                        if (page?.comments?.length > 0) {
                            page.comments = page.comments.filter((val) => val.id !== data.id)
                        }
                        return page
                    }) ?? []

                // Implement optimistic update for comment deletion
                queryClient.setQueryData(infiniteAnswerCommentsQueryKey(answer_id), (data) => ({
                    pages: newPagesArray,
                    pageParams: data.pageParams,
                }))
            },
        }
    )
    return {
        deleteComment: mutateAsync,
        isDeletingComment: isLoading,
    }
}

export const useDeleteAnswersCommentFromEventMutation = () => {
    const queryClient = useQueryClient()
    return ({ detail }) => {
        const answerId = detail?.answer?.id
        const queryKey = infiniteAnswerCommentsQueryKey(answerId)
        const oldPagesArray = queryClient.getQueryData(queryKey)
        const newPagesArray =
            oldPagesArray?.pages.map((page) => {
                if (page?.comments?.length > 0) {
                    page.comments = page.comments.filter((val) => val.id !== detail?.comment?.id)
                }
                return page
            }) ?? []
        queryClient.setQueryData(queryKey, (data) => {
            return {
                pages: newPagesArray,
                pageParams: data.pageParams,
            }
        })
    }
}

export const useLikeAnswersCommentFromEventMutation = () => {
    const queryClient = useQueryClient()
    return ({ detail }) => {
        const answerId = detail?.answer?.id
        const queryKey = infiniteAnswerCommentsQueryKey(answerId)
        const oldPagesArray = queryClient.getQueryData(queryKey)
        const newPagesArray =
            oldPagesArray?.pages.map((page) => {
                if (page?.comments?.length > 0) {
                    page.comments = page.comments.map((val) => {
                        if (val.id === detail?.comment?.id) {
                            val.likes += 1
                        }
                        return val
                    })
                }
                return page
            }) ?? []
        queryClient.setQueryData(queryKey, (data) => {
            return {
                pages: newPagesArray,
                pageParams: data.pageParams,
            }
        })
    }
}

export const useUnlikeAnswersCommentFromEventMutation = () => {
    const queryClient = useQueryClient()
    return ({ detail }) => {
        const answerId = detail?.answer?.id
        const queryKey = infiniteAnswerCommentsQueryKey(answerId)
        const oldPagesArray = queryClient.getQueryData(queryKey)
        const newPagesArray =
            oldPagesArray?.pages.map((page) => {
                if (page?.comments?.length > 0) {
                    page.comments = page.comments.map((val) => {
                        if (val.id === detail?.comment?.id && val.likes > 0) {
                            val.likes -= 1
                        }
                        return val
                    })
                }
                return page
            }) ?? []
        queryClient.setQueryData(queryKey, (data) => {
            return {
                pages: newPagesArray,
                pageParams: data.pageParams,
            }
        })
    }
}

export const useRegenerateMutation = () => {
    return useMutation(
        (id) => axios.put(
            `${process.env.REACT_APP_API_REGENERATE_URL}`,
            { id },
        ),
    )
}